import type { PhysicsRequest } from './physics';
import type { PhysicsReplay } from './types';

interface ReplayWorkerSuccess {
  readonly ok: true;
  readonly replay: PhysicsReplay;
}

interface ReplayWorkerFailure {
  readonly ok: false;
  readonly message: string;
}

type ReplayWorkerResponse = ReplayWorkerSuccess | ReplayWorkerFailure;

function canUseWorker(): boolean {
  return typeof window !== 'undefined' && typeof Worker !== 'undefined';
}

function prepareOnMainThread(request: PhysicsRequest): Promise<PhysicsReplay> {
  return import('./physics').then(({ simulatePhysics }) => simulatePhysics(request));
}

export function preparePhysicsReplay(request: PhysicsRequest): Promise<PhysicsReplay> {
  if (!canUseWorker()) {
    return prepareOnMainThread(request);
  }

  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./physics.worker.ts', import.meta.url), { type: 'module' });
    let settled = false;
    const timeout = window.setTimeout(() => {
      finish(() => reject(new Error('트랙 준비 시간이 길어졌어요. 다시 시도해 주세요.')));
    }, 15_000);
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      worker.terminate();
      callback();
    };

    worker.onmessage = (event: MessageEvent<ReplayWorkerResponse>) => {
      const response = event.data;
      if (response.ok) finish(() => resolve(response.replay));
      else finish(() => reject(new Error(response.message)));
    };
    worker.onerror = () => finish(() => {
      prepareOnMainThread(request).then(resolve, reject);
    });
    worker.postMessage(request);
  });
}
